import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GitCompare, ArrowRight, Check, X, Bookmark, ThumbsUp, Sparkles, Award } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, notifyError, isDailyLimit } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TrustFooter } from "@/components/TrustFooter";
import { ActionCards } from "@/components/ActionCards";
import { SourceImages } from "@/components/SourceImages";

export default function Compare() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [a, setA] = useState(params.get("a") || "");
  const [b, setB] = useState(params.get("b") || "");
  const [ctx, setCtx] = useState("");
  const [budget, setBudget] = useState("");
  const [needs, setNeeds] = useState("");
  const [showAdv, setShowAdv] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);

  const isAuthed = user && typeof user === "object";

  const submit = async (e) => {
    e.preventDefault();
    if (!a.trim() || !b.trim() || loading) return;
    setLoading(true); setError(""); setResult(null); setSaved(false);
    try {
      const { data } = await api.post("/ai/compare", {
        option_a: a.trim(), option_b: b.trim(), context: ctx.trim(),
        budget: budget.trim(), needs: needs.trim(), language,
      });
      setResult(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (!isDailyLimit(detail)) setError(formatApiError(detail) || t("error_generic"));
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!isAuthed) { toast.info(t("result_login_to_save")); return; }
    const c = result.comparison;
    const lines = [
      c.summary,
      ...c.attributes.map((at) => `${at.label}: ${a} — ${at.a} | ${b} — ${at.b}`),
      c.recommendation ? `${t("recommendation")}: ${c.recommendation}` : "",
      c.best_for_you ? `${t("best_for_you")}: ${c.best_for_you}` : "",
    ].filter(Boolean);
    try {
      await api.post("/saved-items", {
        type: "comparison",
        title: `${a} vs ${b}`,
        content: lines.join("\n"),
        tags: ["compare"],
        metadata: { ...result.metadata, comparison: c },
      });
      setSaved(true);
      toast.success(t("comparison_saved"));
    } catch (err) {
      notifyError(err, t);
    }
  };

  const c = result?.comparison;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-50 text-sky-600"><GitCompare className="h-6 w-6" /></span>
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">{t("compare_title")}</h1>
            <p className="text-slate-500">{t("compare_subtitle")}</p>
          </div>
        </div>

        <form onSubmit={submit} className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>{t("option_a")}</Label>
              <Input value={a} onChange={(e) => setA(e.target.value)} placeholder={t("option_a_ph")} data-testid="compare-a-input" className="mt-1.5 min-h-[48px]" required />
            </div>
            <div>
              <Label>{t("option_b")}</Label>
              <Input value={b} onChange={(e) => setB(e.target.value)} placeholder={t("option_b_ph")} data-testid="compare-b-input" className="mt-1.5 min-h-[48px]" required />
            </div>
          </div>
          <div className="mt-4">
            <Label>{t("compare_context")}</Label>
            <Input value={ctx} onChange={(e) => setCtx(e.target.value)} placeholder={t("compare_context_ph")} data-testid="compare-context-input" className="mt-1.5 min-h-[48px]" />
          </div>

          <button type="button" onClick={() => setShowAdv((s) => !s)} data-testid="compare-advanced-toggle" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary">
            <Sparkles className="h-4 w-4" /> {t("advanced_compare")}
          </button>
          {showAdv && (
            <div className="mt-3 grid gap-4 rounded-xl bg-slate-50 p-4 sm:grid-cols-2" data-testid="compare-advanced">
              <div>
                <Label>{t("your_budget")}</Label>
                <Input value={budget} onChange={(e) => setBudget(e.target.value)} placeholder={t("your_budget_ph")} data-testid="compare-budget-input" className="mt-1.5 min-h-[48px]" />
              </div>
              <div>
                <Label>{t("your_needs")}</Label>
                <Input value={needs} onChange={(e) => setNeeds(e.target.value)} placeholder={t("your_needs_ph")} data-testid="compare-needs-input" className="mt-1.5 min-h-[48px]" />
              </div>
            </div>
          )}

          <Button type="submit" disabled={!a.trim() || !b.trim() || loading} data-testid="compare-submit-btn" className="mt-4 w-full gap-1.5 rounded-xl font-semibold min-h-[48px] sm:w-auto sm:px-8">
            {loading ? t("comparing") : (<span className="flex items-center gap-1.5">{t("compare_btn")} <ArrowRight className="h-4 w-4" /></span>)}
          </Button>
        </form>

        {loading && (
          <div className="mt-8 animate-pulse space-y-3" data-testid="compare-loading">
            <div className="h-6 w-1/3 rounded bg-slate-100" />
            <div className="h-40 rounded-2xl bg-slate-100" />
          </div>
        )}

        {error && !loading && (
          <div className="mt-8 rounded-2xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700" data-testid="compare-error">{error}</div>
        )}

        {!result && !loading && !error && (
          <p className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-14 text-center text-slate-500" data-testid="compare-empty">{t("compare_empty")}</p>
        )}

        {c && !loading && (
          <div className="mt-8 space-y-6" data-testid="compare-result">
            {c.summary && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t("compare_summary")}</h2>
                <p className="mt-2 leading-relaxed text-slate-600">{c.summary}</p>
              </div>
            )}

            {c.attributes?.length > 0 && (
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                <div className="grid grid-cols-3 border-b border-slate-100 bg-slate-50 text-sm font-semibold text-slate-900">
                  <div className="p-3" />
                  <div className="p-3 text-center" data-testid="compare-head-a">{a}</div>
                  <div className="p-3 text-center" data-testid="compare-head-b">{b}</div>
                </div>
                {c.attributes.map((at, i) => (
                  <div key={i} className="grid grid-cols-3 border-b border-slate-50 text-sm last:border-0" data-testid={`compare-attr-${i}`}>
                    <div className="p-3 font-medium text-slate-500">{at.label}</div>
                    <div className="p-3 text-slate-700">{at.a}</div>
                    <div className="p-3 text-slate-700">{at.b}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <ProsCons title={a} pros={c.pros_a} cons={c.cons_a} testId="a" />
              <ProsCons title={b} pros={c.pros_b} cons={c.cons_b} testId="b" />
            </div>

            {c.recommendation && (
              <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5" data-testid="compare-recommendation">
                <h2 className="flex items-center gap-2 text-sm font-bold text-primary"><ThumbsUp className="h-4 w-4" /> {t("recommendation")}</h2>
                <p className="mt-2 leading-relaxed text-slate-700">{c.recommendation}</p>
              </div>
            )}

            {c.best_for_you && (
              <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-5" data-testid="compare-best-for-you">
                <h2 className="flex items-center gap-2 text-sm font-bold text-amber-700"><Award className="h-4 w-4" /> {t("best_for_you")}</h2>
                <p className="mt-2 leading-relaxed text-slate-800">{c.best_for_you}</p>
              </div>
            )}

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <SourceImages images={result.metadata?.images} />
              <ActionCards actions={result.metadata?.actions} />
              <TrustFooter metadata={result.metadata} isAuthed={isAuthed} saved={saved} onSave={save} />
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function ProsCons({ title, pros, cons, testId }) {
  const { t } = useLanguage();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`proscons-${testId}`}>
      <h3 className="font-display font-semibold text-slate-900">{title}</h3>
      <div className="mt-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">{t("pros")}</p>
        <ul className="mt-1.5 space-y-1.5">
          {(pros || []).map((p, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-500" />{p}</li>
          ))}
        </ul>
      </div>
      <div className="mt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-rose-600">{t("cons")}</p>
        <ul className="mt-1.5 space-y-1.5">
          {(cons || []).map((cn, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><X className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-rose-500" />{cn}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
